import axios from "axios";
import { getContextoNavegador } from "../utilities/chatEliteContexto";

const urlPath = "chatElite";

/**
 * Error de la API del Chat Elite. `code` es el codigo del back (TICKET_ARCHIVADO, NOT_ALLOWED...).
 */
export class ChatEliteApiError extends Error {
  constructor(code, status) {
    super(code);
    this.name = "ChatEliteApiError";
    this.code = code;
    this.status = status;
  }
}

/**
 * El interceptor global devuelve 401/403/500 como respuesta normal y rechaza el resto,
 * asi que se normalizan los dos casos para que la UI siempre reciba datos o un ChatEliteApiError.
 */
const peticion = async (llamada) => {
  let response;
  try {
    response = await llamada();
  } catch (error) {
    response = error?.response || error;
    if (!response?.status) throw new ChatEliteApiError("ERROR_RED", 0);
  }
  if (response.status >= 400) {
    const error = response.data?.error;
    throw new ChatEliteApiError(typeof error === "string" ? error : "ERROR", response.status);
  }
  return response.data;
};

// Resumen / contadores (AAFF, staff y VENT)
export const getResumenChatElite = () => peticion(() => axios.get(`${urlPath}/resumen`));

// Lado AAFF
export const getMisTicketsChatElite = (bandeja = "activos") =>
  peticion(() => axios.get(`${urlPath}/mis-tickets`, { params: { bandeja } }));

/**
 * Con adjuntos se envia multipart/form-data (campo "archivos"); sin ellos, JSON.
 */
const cuerpo = (campos, archivos = []) => {
  if (!archivos.length) return campos;
  const form = new FormData();
  Object.entries(campos).forEach(([clave, valor]) => {
    if (valor === undefined || valor === null) return;
    form.append(clave, typeof valor === "object" ? JSON.stringify(valor) : String(valor));
  });
  archivos.forEach((archivo) => form.append("archivos", archivo, archivo.name));
  return form;
};

// sinPagina: el AAFF ha quitado el "adjunto" de la pantalla actual (van solo los datos técnicos del navegador)
export const crearTicketChatElite = ({ texto, asunto, archivos = [], sinPagina = false }) =>
  peticion(() => axios.post(`${urlPath}/tickets`, cuerpo({ texto, asunto, contexto: { ...getContextoNavegador(), ...(sinPagina ? { pagina: null } : {}) } }, archivos)));

// Panel staff: { bandeja, busqueda, aaff_id, asignado, pendiente, pagina, limite }
export const getTicketsChatElite = (filtros = {}) =>
  peticion(() => axios.get(`${urlPath}/tickets`, { params: filtros }));

export const getStaffChatElite = () => peticion(() => axios.get(`${urlPath}/staff`));

// Ticket
export const getTicketChatElite = (ticketId) => peticion(() => axios.get(`${urlPath}/tickets/${ticketId}`));

export const getMensajesChatElite = (ticketId, { antesDe, limite } = {}) =>
  peticion(() => axios.get(`${urlPath}/tickets/${ticketId}/mensajes`, { params: { antesDe, limite } }));

// contexto: lado AAFF con la "pantalla actual" puesta (el back la guarda si es otra que la última del ticket)
export const enviarMensajeChatElite = (ticketId, { texto = "", interno = false, archivos = [], contexto = false }) =>
  peticion(() => axios.post(`${urlPath}/tickets/${ticketId}/mensajes`, cuerpo({ texto, interno, ...(contexto ? { contexto: getContextoNavegador() } : {}) }, archivos)));

// Editar / eliminar un mensaje propio que el otro lado aun no ha leido
export const editarMensajeChatElite = (ticketId, mensajeId, texto) =>
  peticion(() => axios.patch(`${urlPath}/tickets/${ticketId}/mensajes/${mensajeId}`, { texto }));

export const eliminarMensajeChatElite = (ticketId, mensajeId) =>
  peticion(() => axios.delete(`${urlPath}/tickets/${ticketId}/mensajes/${mensajeId}`));

// Cambiar el titulo de la consulta (staff)
export const cambiarAsuntoChatElite = (ticketId, asunto) =>
  peticion(() => axios.patch(`${urlPath}/tickets/${ticketId}`, { asunto }));

// URL temporal (caduca en minutos) de un adjunto o de su miniatura
export const getUrlAdjuntoChatElite = (ticketId, adjuntoId, { miniatura = false, descargar = false } = {}) =>
  peticion(() => axios.get(`${urlPath}/tickets/${ticketId}/adjuntos/${adjuntoId}`, { params: { miniatura, descargar } }));

export const marcarLeidoChatElite = (ticketId, ultimoMensajeId) =>
  peticion(() => axios.post(`${urlPath}/tickets/${ticketId}/leer`, { ultimoMensajeId }));

export const resolverTicketChatElite = (ticketId) =>
  peticion(() => axios.post(`${urlPath}/tickets/${ticketId}/resolver`));

export const reabrirTicketChatElite = (ticketId) =>
  peticion(() => axios.post(`${urlPath}/tickets/${ticketId}/reabrir`, { contexto: getContextoNavegador() }));

// Asignaciones (staff). Sin userId se asigna a uno mismo
export const asignarTicketChatElite = (ticketId, userId = null) =>
  peticion(() => axios.post(`${urlPath}/tickets/${ticketId}/asignaciones`, { user_id: userId }));

export const desasignarTicketChatElite = (ticketId, userId) =>
  peticion(() => axios.delete(`${urlPath}/tickets/${ticketId}/asignaciones/${userId}`));
